<template>
  <div class="billing-cockpit">
    <PageHeader icon="fas fa-cash-register">
      <template #title>Billing & Cashier Control</template>
      <template #subtitle
        >Multi-payment, deposit pasien, refund, void, financial clearance, dan
        kontrol shift kasir</template
      >
      <template #actions
        ><button
          class="btn-back"
          @click="$router.push('/payment/detail/' + $route.params.id)"
        >
          <i class="fas fa-arrow-left"></i> Detail Invoice
        </button></template
      >
    </PageHeader>

    <div class="metric-grid" v-if="dashboard.today">
      <article>
        <small>Penerimaan hari ini</small
        ><strong>{{ money(dashboard.today.received) }}</strong>
      </article>
      <article>
        <small>Refund hari ini</small
        ><strong>{{ money(dashboard.today.refunded) }}</strong>
      </article>
      <article>
        <small>Invoice terbuka</small
        ><strong>{{ dashboard.today.open_invoices }}</strong>
      </article>
      <article>
        <small>Piutang aktif</small
        ><strong>{{ money(dashboard.today.unpaid) }}</strong>
      </article>
      <article>
        <small>Settlement periode ini</small
        ><strong>{{
          money(reports.settlement && reports.settlement.total)
        }}</strong>
      </article>
      <article>
        <small>Menunggu approval</small
        ><strong>{{ reports.pending_approvals || 0 }}</strong>
      </article>
    </div>

    <div class="main-grid">
      <section class="panel invoice-panel">
        <div class="panel-head">
          <div>
            <span>FINANCIAL CLEARANCE</span>
            <h3>Invoice #{{ payment.invoice_number || payment.id }}</h3>
          </div>
          <b :class="['status', payment.workflow_status]">{{
            label(payment.workflow_status || "draft")
          }}</b>
        </div>
        <div class="patient">
          <i class="fas fa-user-injured"></i>
          <div>
            <strong>{{ payment.patient_name }}</strong
            ><small>{{
              payment.patient_mobile_phone || "Nomor telepon belum tersedia"
            }}</small>
          </div>
        </div>
        <div class="amount-grid">
          <div>
            <small>Total tagihan</small
            ><strong>{{ money(payment.final_amount) }}</strong>
          </div>
          <div>
            <small>Terbayar bersih</small
            ><strong>{{ money(clearance.net_paid) }}</strong>
          </div>
          <div>
            <small>Deposit dipakai</small
            ><strong>{{ money(payment.deposit_applied) }}</strong>
          </div>
          <div>
            <small>Sisa</small
            ><strong class="danger">{{ money(clearance.remaining) }}</strong>
          </div>
        </div>
        <div :class="['clearance', clearance.is_cleared ? 'ok' : 'hold']">
          <i
            :class="
              clearance.is_cleared
                ? 'fas fa-check-circle'
                : 'fas fa-exclamation-triangle'
            "
          ></i>
          <div>
            <b>{{
              clearance.is_cleared
                ? "Financial clearance selesai"
                : "Financial hold"
            }}</b
            ><small>{{
              clearance.is_cleared
                ? "Pasien dapat menyelesaikan proses layanan."
                : "Selesaikan pembayaran atau validasi penjamin."
            }}</small>
          </div>
        </div>
      </section>

      <section class="panel shift-panel">
        <div class="panel-head">
          <div>
            <span>SHIFT KASIR</span>
            <h3>
              {{
                dashboard.shift ? "Shift sedang aktif" : "Belum ada shift aktif"
              }}
            </h3>
          </div>
          <i class="fas fa-user-clock"></i>
        </div>
        <template v-if="dashboard.shift"
          ><p>
            Dibuka {{ dateTime(dashboard.shift.opened_at) }} oleh
            {{ dashboard.shift.cashier_name }}
          </p>
          <label
            >Kas fisik saat penutupan<input
              type="number"
              v-model.number="closingCash" /></label
          ><button class="secondary" @click="closeShift">
            Tutup & Rekonsiliasi Shift
          </button></template
        >
        <template v-else
          ><label
            >Modal kas awal<input
              type="number"
              v-model.number="openingCash" /></label
          ><button class="primary" @click="openShift">
            Buka Shift Kasir
          </button></template
        >
      </section>
    </div>

    <div class="action-grid">
      <section class="panel">
        <div class="panel-head">
          <div>
            <span>MULTI-PAYMENT</span>
            <h3>Tambah pembayaran</h3>
          </div>
          <i class="fas fa-layer-group"></i>
        </div>
        <div class="allocation" v-for="(a, i) in allocations" :key="i">
          <select v-model="a.payment_method_id">
            <option value="">Metode</option>
            <option v-for="m in methods" :key="m.id" :value="m.id">
              {{ m.name }}
            </option></select
          ><input
            type="number"
            v-model.number="a.amount"
            placeholder="Nominal"
          /><input
            v-model="a.reference_number"
            placeholder="Referensi"
          /><button @click="allocations.splice(i, 1)">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <button class="link-btn" @click="allocations.push(blankAllocation())">
          <i class="fas fa-plus"></i> Tambah metode</button
        ><button class="primary wide" :disabled="!dashboard.shift" @click="pay">
          Proses Pembayaran</button
        ><small v-if="!dashboard.shift" class="hint"
          >Buka shift kasir sebelum menerima pembayaran.</small
        >
      </section>

      <section class="panel">
        <div class="panel-head">
          <div>
            <span>DEPOSIT PASIEN</span>
            <h3>Saldo {{ money(clearance.deposit_balance) }}</h3>
          </div>
          <i class="fas fa-wallet"></i>
        </div>
        <label
          >Gunakan untuk invoice<input
            type="number"
            v-model.number="depositUse"
            :max="
              Math.min(clearance.deposit_balance, clearance.remaining)
            " /></label
        ><button class="primary wide" @click="applyDeposit">
          Gunakan Deposit
        </button>
        <hr />
        <label
          >Tambah saldo deposit<input
            type="number"
            v-model.number="depositTopup" /></label
        ><label
          >Metode pembayaran<select v-model="depositMethod">
            <option v-for="m in methods" :key="m.id" :value="m.id">
              {{ m.name }}
            </option>
          </select></label
        ><button
          class="secondary wide"
          :disabled="!dashboard.shift"
          @click="topup"
        >
          Top Up Deposit
        </button>
      </section>

      <section class="panel danger-panel">
        <div class="panel-head">
          <div>
            <span>KOREKSI TERKONTROL</span>
            <h3>Refund / Void</h3>
          </div>
          <i class="fas fa-shield-alt"></i>
        </div>
        <p class="approval-note">
          <i class="fas fa-user-shield"></i> Koreksi diajukan untuk persetujuan
          supervisor. Pemohon tidak dapat menyetujui permintaannya sendiri.
        </p>
        <label
          >Alasan<select v-model="adjust.reason_code">
            <option value="input_error">Kesalahan input</option>
            <option value="service_cancelled">Layanan dibatalkan</option>
            <option value="duplicate">Transaksi duplikat</option>
            <option value="other">Lainnya</option>
          </select></label
        ><label
          >Catatan alasan<textarea
            v-model="adjust.reason"
            placeholder="Wajib diisi untuk audit trail"
          ></textarea></label
        ><label
          >Nominal refund<input type="number" v-model.number="adjust.amount"
        /></label>
        <div class="two">
          <button class="refund" @click="refundPayment">Ajukan Refund</button
          ><button class="void" @click="voidInvoice">Ajukan Void</button>
        </div>
      </section>
    </div>

    <section class="panel history">
      <div class="panel-head">
        <div>
          <span>AUDIT TRANSAKSI</span>
          <h3>Alokasi & koreksi pembayaran</h3>
        </div>
      </div>
      <div
        class="history-row"
        v-for="a in payment.allocations || []"
        :key="'a' + a.id"
      >
        <i class="fas fa-check-circle"></i
        ><span
          >{{ label(a.method && a.method.name)
          }}<small
            >{{ dateTime(a.paid_at) }} ·
            {{ a.reference_number || "Tanpa referensi" }}</small
          ></span
        ><b>{{ money(a.amount) }}</b>
      </div>
      <div
        class="history-row adjustment"
        v-for="a in payment.adjustments || []"
        :key="'x' + a.id"
      >
        <i class="fas fa-undo"></i
        ><span
          >{{ label(a.type) }}<small>{{ a.reason }}</small></span
        ><b>-{{ money(a.amount) }}</b>
      </div>
      <p
        class="empty"
        v-if="
          !(payment.allocations || []).length &&
          !(payment.adjustments || []).length
        "
      >
        Belum ada transaksi pada ledger baru.
      </p>
    </section>

    <section
      class="panel history"
      v-if="(payment.action_requests || []).length"
    >
      <div class="panel-head">
        <div>
          <span>APPROVAL SUPERVISOR</span>
          <h3>Permintaan koreksi invoice</h3>
        </div>
      </div>
      <div
        class="history-row approval-row"
        v-for="a in payment.action_requests"
        :key="'r' + a.id"
      >
        <i class="fas fa-user-shield"></i
        ><span
          ><b>{{ a.request_number }} · {{ label(a.action_type) }}</b
          ><small
            >{{ a.requested_by_name }} · {{ dateTime(a.requested_at) }} ·
            {{ a.reason }}</small
          ></span
        >
        <div v-if="a.status === 'pending'" class="approval-actions">
          <button @click="review(a, 'approved')">Setujui</button
          ><button class="reject" @click="review(a, 'rejected')">Tolak</button>
        </div>
        <b v-else :class="['request-status', a.status]">{{
          label(a.status)
        }}</b>
      </div>
    </section>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import module from "@/core/modules/CrudModule.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
export default {
  data: () => ({
    dashboard: { today: {} },
    reports: { settlement: {} },
    clearance: {},
    payment: {},
    methods: [],
    allocations: [{ payment_method_id: "", amount: 0, reference_number: "" }],
    openingCash: 0,
    closingCash: 0,
    depositUse: 0,
    depositTopup: 0,
    depositMethod: 1,
    adjust: { amount: 0, reason_code: "input_error", reason: "" },
  }),
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Keuangan" },
      { title: "Pembayaran", route: "/payment/list" },
      { title: "Billing Control" },
    ]);
    this.load();
  },
  methods: {
    async load() {
      const [d, c, m, r] = await Promise.all([
        ApiService.get("billing/dashboard"),
        ApiService.get(`billing/payments/${this.$route.params.id}/clearance`),
        module.list("payment-methods"),
        ApiService.get("billing/reports"),
      ]);
      this.dashboard = d.data.data || d.data;
      this.clearance = c.data.data || c.data;
      this.payment = this.clearance.payment || {};
      this.methods = m || [];
      this.reports = r.data.data || r.data;
      if (this.methods[0] && !this.depositMethod)
        this.depositMethod = this.methods[0].id;
    },
    blankAllocation() {
      return { payment_method_id: "", amount: 0, reference_number: "" };
    },
    idempotencyKey() {
      return `pay-${this.payment.id}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
    },
    async run(url, payload, msg) {
      try {
        await ApiService.post(url, payload);
        await this.load();
        this.$bvToast.toast(msg, {
          title: "Berhasil",
          variant: "success",
          solid: true,
        });
      } catch (e) {
        this.$bvToast.toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Transaksi belum dapat diproses.",
          { title: "Periksa kembali", variant: "danger", solid: true }
        );
      }
    },
    openShift() {
      this.run(
        "billing/shifts/open",
        { opening_cash: this.openingCash },
        "Shift kasir dibuka."
      );
    },
    closeShift() {
      this.run(
        `billing/shifts/${this.dashboard.shift.id}/close`,
        { closing_cash: this.closingCash },
        "Shift ditutup dan direkonsiliasi."
      );
    },
    pay() {
      this.run(
        `billing/payments/${this.payment.id}/allocate`,
        {
          cashier_shift_id: this.dashboard.shift.id,
          allocations: this.allocations,
          idempotency_key: this.idempotencyKey(),
        },
        "Pembayaran berhasil dialokasikan."
      );
      this.allocations = [this.blankAllocation()];
    },
    applyDeposit() {
      this.run(
        `billing/payments/${this.payment.id}/apply-deposit`,
        { amount: this.depositUse },
        "Deposit berhasil digunakan."
      );
    },
    topup() {
      this.run(
        "billing/deposits/topup",
        {
          patient_id: this.payment.patient_id,
          cashier_shift_id: this.dashboard.shift && this.dashboard.shift.id,
          amount: this.depositTopup,
          payment_method_id: this.depositMethod,
        },
        "Deposit pasien berhasil ditambahkan."
      );
    },
    refundPayment() {
      this.run(
        `billing/payments/${this.payment.id}/refund`,
        this.adjust,
        "Permintaan refund dikirim ke supervisor."
      );
    },
    voidInvoice() {
      if (!confirm("Ajukan void invoice ini kepada supervisor?")) return;
      this.run(
        `billing/payments/${this.payment.id}/void`,
        this.adjust,
        "Permintaan void dikirim ke supervisor."
      );
    },
    review(a, decision) {
      const note = prompt(
        decision === "approved"
          ? "Catatan persetujuan (opsional)"
          : "Alasan penolakan"
      );
      if (note === null) return;
      this.run(
        `billing/actions/${a.id}/review`,
        { decision, review_notes: note },
        decision === "approved"
          ? "Permintaan disetujui."
          : "Permintaan ditolak."
      );
    },
    money(v) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(v || 0);
    },
    label(v) {
      return String(v || "-")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    dateTime(v) {
      return this.$clinicDateTime(v, { dateStyle: "medium", timeStyle: "short" });
    },
  },
};
</script>

<style scoped>
.billing-cockpit {
  padding-bottom: 30px;
  color: #25344a;
}
.metric-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin: 18px 0;
}
.metric-grid article,
.panel {
  background: #fff;
  border: 1px solid #dce7ef;
  border-radius: 16px;
  box-shadow: 0 10px 28px rgba(28, 61, 96, 0.055);
}
.metric-grid article {
  padding: 16px;
}
.metric-grid small {
  display: block;
  color: #71839a;
  font-size: 11px;
}
.metric-grid strong {
  display: block;
  margin-top: 5px;
  font-size: 18px;
}
.main-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
}
.panel {
  padding: 18px;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 15px;
}
.panel-head span {
  color: #15928c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.panel-head h3 {
  margin: 3px 0 0;
  font-size: 16px;
}
.panel-head > i {
  font-size: 22px;
  color: #168f8a;
}
.patient {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: #f5f9fb;
  border-radius: 12px;
}
.patient i {
  color: #167a9a;
  font-size: 22px;
}
.patient small,
.patient strong {
  display: block;
}
.amount-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 9px;
  margin: 14px 0;
}
.amount-grid div {
  padding: 11px;
  background: #f8fafc;
  border-radius: 10px;
}
.amount-grid small,
.amount-grid strong {
  display: block;
}
.amount-grid small {
  font-size: 10px;
  color: #72839a;
}
.amount-grid strong {
  margin-top: 4px;
  font-size: 14px;
}
.danger {
  color: #e0434a;
}
.clearance {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 11px;
}
.clearance.ok {
  background: #eaf8f1;
  color: #148153;
}
.clearance.hold {
  background: #fff5e6;
  color: #ad6a00;
}
.clearance small,
.clearance b {
  display: block;
}
.status {
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef4f8;
  font-size: 10px;
}
.status.paid,
.status.guaranteed {
  background: #e4f7ee;
  color: #118252;
}
.shift-panel label,
label {
  display: block;
  color: #52667d;
  font-size: 11px;
  font-weight: 700;
  margin: 10px 0;
}
input,
select,
textarea {
  width: 100%;
  min-height: 40px;
  margin-top: 5px;
  padding: 9px 11px;
  border: 1px solid #d4e1eb;
  border-radius: 9px;
  background: #fff;
  color: #26364b;
}
textarea {
  min-height: 68px;
}
.primary,
.secondary,
.refund,
.void,
.btn-back {
  min-height: 40px;
  padding: 9px 13px;
  border: 0;
  border-radius: 9px;
  color: #fff;
  font-size: 11px;
  font-weight: 750;
}
.primary {
  background: linear-gradient(100deg, #154f82, #16958e);
}
.secondary {
  background: #557088;
}
.wide {
  width: 100%;
}
.action-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 14px;
}
.allocation {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 32px;
  gap: 6px;
}
.allocation button,
.link-btn {
  border: 0;
  background: transparent;
  color: #167e8c;
}
.link-btn {
  padding: 7px;
}
.hint {
  display: block;
  margin-top: 7px;
  color: #ae6a00;
}
.approval-note {
  padding: 10px;
  border-radius: 10px;
  background: #f3f7fb;
  color: #5c6f85;
  font-size: 10px;
}
.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.refund {
  background: #e09525;
}
.void {
  background: #ca4148;
}
.history {
  margin-top: 14px;
}
.history-row {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 11px;
  border-top: 1px solid #edf1f5;
}
.history-row i {
  color: #16905d;
}
.history-row span small {
  display: block;
  color: #7b8ca0;
}
.history-row.adjustment i,
.history-row.adjustment b {
  color: #d6444b;
}
.approval-actions {
  display: flex;
  gap: 6px;
}
.approval-actions button {
  border: 0;
  border-radius: 7px;
  padding: 7px 10px;
  background: #168a63;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}
.approval-actions .reject {
  background: #cf4b52;
}
.request-status {
  padding: 5px 8px;
  border-radius: 999px;
  background: #e9f7ef;
  color: #168252;
  font-size: 10px;
}
.request-status.rejected {
  background: #fdecee;
  color: #c33c46;
}
.empty {
  text-align: center;
  color: #8a9aad;
}
.btn-back {
  background: #fff;
  color: #155487;
  border: 1px solid #d5e1eb;
}
.panel hr {
  border: 0;
  border-top: 1px solid #e6edf2;
  margin: 16px 0;
}
@media (max-width: 1200px) {
  .metric-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 992px) {
  .metric-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .main-grid,
  .action-grid {
    grid-template-columns: 1fr;
  }
  .amount-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 576px) {
  .metric-grid {
    grid-template-columns: 1fr 1fr;
  }
  .allocation {
    grid-template-columns: 1fr 1fr;
  }
  .allocation input:nth-of-type(2) {
    grid-column: 1/2;
  }
  .amount-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
